<template #default>
    <form
        v-if="status.detail"
        @submit.prevent="groupID ? requestUpdate() : requestCreate()"
    >
        <b-form-group
            :label="$t('label.image')"
            label-cols="4"
            label-align="left"
        >
            <ImageUploader
                :key="groupID"
                :multiple="false"
                :showImageTop="true"
                :buttonText="$t('label.uploadX', [$t('label.image')])"
                @images-changed="uploadImage"
                @images-removed="removeImage"
                :initial-images="createForm.image ? [createForm.image] : []"
                ref="coverUploader"
            />
            <div v-if="submitted && !$v.createForm.image.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.image")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.name')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.name"
                :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required">
                {{ $t("message.requiredX", [$t("label.name")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.apiCode')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.apiCode"
                :class="{ 'is-invalid': submitted && !$v.createForm.apiCode.required }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.apiCode.required">
                {{ $t("message.requiredX", [$t("label.apiCode")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.color')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                type="color"
                v-model.trim="createForm.colorCode"
                :class="{ 'is-invalid': submitted && !$v.createForm.colorCode.required }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.colorCode.required">
                {{ $t("message.requiredX", [$t("label.color")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.websiteUrl')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.websiteUrl"
                :class="{ 'is-invalid': submitted && !$v.createForm.websiteUrl.required }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.websiteUrl.required">
                {{ $t("message.requiredX", [$t("label.websiteUrl")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.investorUrl')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.investorUrl"
                :class="{ 'is-invalid': submitted && !$v.createForm.investorUrl.required }"
            ></b-form-input>
            <b-form-invalid-feedback v-if="submitted && !$v.createForm.investorUrl.required">
                {{ $t("message.requiredX", [$t("label.investorUrl")]) }}
            </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
            :label="$t('label.services')"
            label-cols="4"
            label-align="left"
        >
            <multiselect
                v-model="createForm.services"
                :options="[]"
                :multiple="true"
                :taggable="true"
                :placeholder="$t('message.pressEnterToAddX', [])"
                :tag-placeholder="$t('message.pressEnterToAddX', [$t('label.services')])"
                :show-options="false"
                :show-no-options="false"
                v-on:tag="addTag"
            />
            <div v-if="submitted && !$v.createForm.services.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.services")]) }}
            </div>
        </b-form-group>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                :disabled="!status.save"
            >{{ groupID ? $t('label.updateX', []) : $t('label.createX', []) }}</b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import { groupMethods } from "@/state/helpers";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { GROUP_MODULE } from '@/state/modules/group';
import ImageUploader from '@/components/upload-image';
import Multiselect from "vue-multiselect";

export default {
    props: {
        groupID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    components: { ImageUploader, Multiselect },
    async created() {
        await this.getGroupData();
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
        };
    },
    validations() {
        return {
			createForm: {
				name: { required },
				apiCode: { required },
				colorCode: { required },
				websiteUrl: { required },
				investorUrl: { required },
				image: { required },
                services: { required },
			},
		};
    },
    methods: {
        ...groupMethods,
        defaultForm() {
            return {
                id: "",
                name: "",
                image: null,
                apiCode: "",
                colorCode: "",
                websiteUrl: "",
                investorUrl: "",
                services: [],
            };
        },
        async getGroupData() {
            if (this.groupID) {
                await this.getGroup({ id: this.groupID });
                this.createForm = {
                    id: this.data.id,
                    name: this.data.name,
                    image: this.data.image,
                    apiCode: this.data.apiCode,
                    colorCode: this.data.colorCode,
                    websiteUrl: this.data.websiteUrl,
                    investorUrl: this.data.investorUrl,
                    services: Array.isArray(this.data.services) ? [...this.data.services] : [],
                };
            }
        },
        uploadImage(image) {
            this.createForm.image = image[0]?.file;
        },
        removeImage() {
            this.createForm.image = null;
        },
        addTag(newTag) {
            const tag = newTag.trim();
            if (tag && !this.createForm.services.includes(tag)) {
                this.createForm.services.push(tag);
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
					...this.createForm,
                }
                this.createGroup({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
					...this.createForm,
                }
                if(this.createForm.image instanceof File) {
                    data.image = this.createForm.image;
                }
                this.updateGroup({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
            this.$refs.coverUploader?.clear?.();
        },
    },
    computed: {
        ...mapState(GROUP_MODULE, ['status']),
        data() {
            return this.$store.state.group.details;
        },
    },
}
</script>
